<script setup>
import {computed, ref} from 'vue'
import {useRouter} from "vue-router"
import SiteTreeNavigationMenu from "@/common/site-tree/SiteTreeNavigationMenu.vue"
import SiteTree from "@/common/site-tree/SiteTree.vue"
import {useSiteTreeStore} from '@/stores/siteTree'
import {useToastsStore} from '@/stores/toasts'
import {usePageContentDataStore} from '@/stores/pageContentData'
import {usePageEditorStore} from '@/stores/pageEditor'
import {useItemDataStore} from '@/stores/itemData'

const siteTreeId = 'siteTreeOffCanvas'

const router = useRouter()

const siteTreeStore = useSiteTreeStore(siteTreeId)
const toastsStore = useToastsStore()
const pageContentDataStore = usePageContentDataStore()
const pageEditorStore = usePageEditorStore()
const itemDataStore = useItemDataStore()

const closeBtn = ref(null)

const offcanvasEnd = computed(() => pageEditorStore.offcanvasEnd)
const pageId = computed(() => pageEditorStore.pageId)
const activePageContentId = computed(() => pageEditorStore.activePageContentId)

const activeContent = computed(() => pageEditorStore.contentItems.find(o => o.pageContentId === activePageContentId.value))
const contentAreaName = computed(() => pageEditorStore.activeContentAreaName || activeContent.value?.contentAreaName)
const contentArea = computed(() => pageEditorStore.contentAreas.find(o => o.name === contentAreaName.value))

siteTreeStore.init({
    selectContentMode: true,
    onContentSelect: async (pageContentId) => {
        const contentId = pageContentDataStore.items.find(o => o.id === pageContentId)?.contentId

        const content = await itemDataStore.get({
            tableName: 'content',
            id: contentId
        })

        const contentType = await itemDataStore.get({
            tableName: 'templates_templates',
            id: content.contentType
        })

        const type = contentType.contentType
        const componentId = contentType.componentId

        if (type === 'Listing' || type === 'Listing component') {
            const page = await itemDataStore.get({
                tableName: 'pages',
                id: pageId.value
            })

            let error = ''
            if (page.type !== 'Listing') {
                error = 'Only listing pages can support this content.'
            } else if (type === 'Listing' && componentId !== page.componentId) {
                error = 'This listing page does not support this content.'
            } else if (type === 'Listing component' && componentId > 0 && componentId !== page.componentId) {
                error = 'This listing page does not support this content.'
            }

            if (error) {
                await toastsStore.add({body: error, colourScheme: 'warning'})
                return
            }
        }

        const newPageContentId = await pageEditorStore.addContentToPage({
            contentId: contentId,
            pageSectionLinkId: contentArea.value.sectionId,
            contentAreaName: contentAreaName.value
        })

        await pageEditorStore.goToContent({
            router: router,
            pageContentId: newPageContentId
        })

        closeBtn.value?.click()
    }
})
</script>

<template>
    <div ref="offcanvas" id="addExistingContentOffcanvas" aria-labelledby="addExistingContentOffcanvasLabel"
         class="offcanvas" :class="{'offcanvas-start': !offcanvasEnd, 'offcanvas-end': offcanvasEnd}" tabindex="-1">
        <div class="offcanvas-header">
            <SiteTreeNavigationMenu :site-tree-id="siteTreeId" class="w-auto"/>
            <button ref="closeBtn" type="button" class="btn-close" data-bs-dismiss="offcanvas"
                    aria-label="Close"></button>
        </div>

        <div class="offcanvas-body">
            <SiteTree :site-tree-id="siteTreeId"/>
        </div>
    </div>
</template>